import type RoutesPlannerStore from '../routes-planner-store';
import type { Driver } from '@bringg/types';
import type { ConsecutiveRoutes, RoutesPlannerRun, RoutesPlannerTask } from '../../types/routes-planner-run';
import type { RoutesPlannerBreak } from '../../components/routes-planner/routes-planner';
import type { EtaResponseRouteItem, EtaResponseTaskItem } from '@bringg-frontend/eta-calculator';
import { computed, makeObservable, observable, action } from 'mobx';
import _groupBy from 'lodash/groupBy';
import { createDriverByRunIdMap } from '../../components/routes-planner/utils';

export default class ConsecutiveRoutesStore {
	rootStore: RoutesPlannerStore;
	routesById: Map<number, RoutesPlannerRun> = new Map();
	tasksById: Map<number, RoutesPlannerTask> = new Map();
	runToBreaks: { [runId: number]: RoutesPlannerBreak[] } = {};

	constructor(rootStore: RoutesPlannerStore, consecutiveRoutes: ConsecutiveRoutes) {
		this.rootStore = rootStore;

		makeObservable(this, {
			routesById: observable,
			tasksById: observable,
			runToBreaks: observable,
			updateRouteById: action,
			setRoutes: action,
			batchUpdateRoutes: action,
			updateTaskById: action,
			batchUpdateTasks: action,
			batchUpdateBreaks: action,
			routesTasksByRouteId: computed
		});

		this.setRoutes(consecutiveRoutes);
	}

	setRoutes = (consecutiveRoutes: ConsecutiveRoutes) => {
		this.routesById = new Map(consecutiveRoutes.routesById);
		this.tasksById = new Map(consecutiveRoutes.tasksById);
	};

	get routesTasksByRouteId(): { [runId: number]: RoutesPlannerTask[] } {
		const consecutiveRoutesTasks = Array.from(this.tasksById.values());
		return _groupBy(consecutiveRoutesTasks, 'run_id');
	}

	get routesToDriver(): { [runId: number]: Driver } {
		return createDriverByRunIdMap(this.rootStore.driversById, this.tasksById);
	}

	updateRouteById = (routeId: number, updatedData: Partial<RoutesPlannerRun> | Partial<EtaResponseRouteItem>) => {
		const hasRoute = this.routesById.has(routeId);
		if (hasRoute) {
			const updatedRoute: RoutesPlannerRun = {
				...this.routesById.get(routeId),
				...updatedData,
				vehicle_id:
					(updatedData as EtaResponseRouteItem).power_unit_id || (updatedData as RoutesPlannerRun).vehicle_id
			};
			this.routesById.set(routeId, updatedRoute);
		}
	};

	batchUpdateRoutes = (routes: EtaResponseRouteItem[] | RoutesPlannerRun[]) => {
		routes.forEach(routeInfo => {
			this.updateRouteById(routeInfo.run_id || routeInfo.id, routeInfo);
		});
	};

	updateTaskById = (taskId: number, updatedData: Partial<RoutesPlannerTask>) => {
		const hasTask = this.tasksById.has(taskId);
		if (hasTask) {
			const updatedTask = { ...this.tasksById[taskId], ...updatedData };
			this.tasksById.set(taskId, updatedTask);
		}
	};

	batchUpdateTasks = (tasks: EtaResponseTaskItem[] | RoutesPlannerTask[]) => {
		tasks.forEach(taskInfo => {
			this.updateTaskById(taskInfo.task_id || taskInfo.id, taskInfo);
		});
	};

	batchUpdateBreaks = (breaks: RoutesPlannerBreak[]) => {
		breaks.forEach(breakItem => {
			const foundedBreak = this.runToBreaks[breakItem.run_id];
			if (foundedBreak) {
				foundedBreak.push(breakItem);
			} else {
				this.runToBreaks[breakItem.run_id] = [breakItem];
			}
		});
	};
}
